import SkeletonAvatar from "antd/lib/skeleton/Avatar";
import { useRouter } from "next/router";
import { ReactElement, useState } from "react";
import { I18Text } from "src/types/i18Text";
import { LabelType } from "src/types/label";
import Link from "next/link";
import { GLOBAL_PARAMS } from "src/constants/params";
import { ProductCategory as ProductCategoryType } from "src/types/productCategory";
import ProductTypes from "src/types/productType";
import { filterParams, formatPathByLogicalGroupChannel } from "src/utils/url";
import {
  Card,
  CardContainer,
  CardDetailContainer,
  DesktopTitle,
  MobileTitle,
  InsurerImage,
  CategoryImage,
  LogoTitleContainer,
  SkeletonTitle,
} from "./LandingPageRetailCategoryCard.style";

export type RetailCategoryCardProps = {
  isLoading: boolean;
  insurerIcon?: string;
  categoryIcon?: string;
  displayName?: I18Text;
  labels?: LabelType[];
};

const LandingPageRetailCategoryCard = ({
  isLoading,
  insurerIcon,
  categoryIcon,
  displayName,
  labels,
  ...props
}: RetailCategoryCardProps): ReactElement => {
  const { locale } = useRouter();
  const [isImageError, setIsImageError] = useState<boolean>(false);
  const onImageError = () => {
    setIsImageError(true);
  };

  return (
    <CardContainer {...props}>
      <Card
        bodyStyle={{
          padding: "16px",
          width: "100%",
          ...(insurerIcon ? { height: "100%" } : { margin: "auto" }),
        }}
      >
        <CardDetailContainer>
          <LogoTitleContainer>
            {insurerIcon && !isImageError && !isLoading && (
              <InsurerImage
                src={insurerIcon}
                alt="Insurer Icon"
                onError={onImageError}
              />
            )}
            {isLoading ? (
              <SkeletonTitle loading paragraph={false} title={{ width: 140 }} />
            ) : (
              <>
                <DesktopTitle style={insurerIcon ? {} : { margin: "auto" }}>
                  {displayName?.[locale]}
                </DesktopTitle>
                <MobileTitle style={insurerIcon ? {} : { margin: "auto" }}>
                  {displayName?.[locale]}
                </MobileTitle>
              </>
            )}
          </LogoTitleContainer>
          {isImageError || isLoading ? (
            <SkeletonAvatar size={64} />
          ) : (
            <CategoryImage
              src={categoryIcon}
              alt="Category Icon"
              onError={onImageError}
              style={
                insurerIcon ? {} : { marginTop: "auto", marginBottom: "auto" }
              }
            />
          )}
        </CardDetailContainer>
      </Card>
    </CardContainer>
  );
};

type ProductLinkMapperProps = {
  path: string;
  productCategory: ProductCategoryType;
  insurerIcon: string;
  categoryIcon: string;
};

const ProductLinkMapper = ({
  path,
  productCategory,
  insurerIcon = "",
  categoryIcon,
}: ProductLinkMapperProps) => {
  const { code, displayName, labels } = productCategory;
  const router = useRouter();
  return (
    <Link
      href={{
        pathname: path,
        query:
          productCategory.type === ProductTypes.static ||
          productCategory.type === ProductTypes.external ||
          productCategory.type === ProductTypes.dynamic
            ? filterParams(router.query, GLOBAL_PARAMS)
            : {},
      }}
      passHref
      legacyBehavior
    >
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a>
        <LandingPageRetailCategoryCard
          isLoading={false}
          key={code}
          displayName={displayName}
          insurerIcon={insurerIcon}
          categoryIcon={categoryIcon}
          labels={labels}
          data-cy={`landing-page.product-card.${code}`}
        />
      </a>
    </Link>
  );
};

type GetProductCategoryCardParams = {
  productCategory: ProductCategoryType;
  channelPath: string;
};

export const getLandingPageRetailProductCategoryCard = ({
  productCategory,
  channelPath,
}: GetProductCategoryCardParams) => {
  switch (productCategory.type) {
    case ProductTypes.static:
      return (
        <ProductLinkMapper
          productCategory={productCategory}
          path={formatPathByLogicalGroupChannel(
            `/${channelPath}/${productCategory.code}/plan`
          )}
          insurerIcon={productCategory.icon.insurer}
          categoryIcon={productCategory.icon.url}
        />
      );
    case ProductTypes.dynamic:
      return (
        <ProductLinkMapper
          productCategory={productCategory}
          path={formatPathByLogicalGroupChannel(
            `/${channelPath}/${productCategory.code}`
          )}
          insurerIcon={productCategory.icon.insurer}
          categoryIcon={productCategory.icon.url}
        />
      );
    case ProductTypes.external:
      return (
        <ProductLinkMapper
          productCategory={productCategory}
          path={productCategory.externalLink}
          insurerIcon={productCategory.icon.insurer}
          categoryIcon={productCategory.icon.url}
        />
      );
    default:
      return (
        <LandingPageRetailCategoryCard
          isLoading={false}
          key={productCategory.code}
          displayName={productCategory.displayName}
          insurerIcon={productCategory.icon.insurer}
          categoryIcon={productCategory.icon.url}
          labels={productCategory.labels}
        />
      );
  }
};

export default LandingPageRetailCategoryCard;
